import { create } from 'zustand'
import type { Note } from '@/types'

/**
 * 素材选择器目标
 */
interface AssetTarget {
  noteId: string
  index: number
}

/**
 * 笔记 UI 状态 Store
 * 管理选中状态、弹窗状态等 UI 交互状态
 */
interface NoteUIState {
  // ========== 选中状态 ==========
  selectedIds: string[]
  toggleSelect: (noteId: string) => void
  selectAll: (noteIds: string[]) => void
  clearSelection: () => void

  // ========== 详情弹窗 ==========
  detailNote: Note | null
  openDetail: (note: Note) => void
  closeDetail: () => void
  updateDetailNote: (note: Note) => void
  updateDetailImages: (images: Note['images']) => void
  updateDetailStatus: (status: Note['status']) => void

  // ========== 素材选择器 ==========
  assetPickerOpen: boolean
  assetTarget: AssetTarget
  openAssetPicker: (noteId: string, index: number) => void
  closeAssetPicker: () => void

  // ========== 模版选择器 ==========
  templatePickerOpen: boolean
  pendingAction: 'bulk' | 'smart' | null
  pendingNote: Note | null
  openTemplatePicker: (action: 'bulk' | 'smart', note?: Note) => void
  closeTemplatePicker: () => void

  // ========== 重置 ==========
  reset: () => void
}

const initialState = {
  // 选中状态
  selectedIds: [] as string[],
  // 详情弹窗
  detailNote: null as Note | null,
  // 素材选择器
  assetPickerOpen: false,
  assetTarget: { noteId: '', index: -1 } as AssetTarget,
  // 模版选择器
  templatePickerOpen: false,
  pendingAction: null as 'bulk' | 'smart' | null,
  pendingNote: null as Note | null,
}

export const useNoteUIStore = create<NoteUIState>((set, get) => ({
  ...initialState,

  // ========== 选中状态 ==========
  toggleSelect: (noteId: string) => {
    set((state) => ({
      selectedIds: state.selectedIds.includes(noteId)
        ? state.selectedIds.filter((id) => id !== noteId)
        : [...state.selectedIds, noteId],
    }))
  },

  selectAll: (noteIds: string[]) => {
    set({ selectedIds: noteIds })
  },

  clearSelection: () => {
    set({ selectedIds: [] })
  },

  // ========== 详情弹窗 ==========
  openDetail: (note: Note) => {
    set({ detailNote: note })
  },

  closeDetail: () => {
    set({ detailNote: null })
  },

  updateDetailNote: (note: Note) => {
    const { detailNote } = get()
    if (detailNote?.id === note.id) {
      set({ detailNote: note })
    }
  },

  updateDetailImages: (images: Note['images']) => {
    const { detailNote } = get()
    if (detailNote) {
      set({ detailNote: { ...detailNote, images } })
    }
  },

  updateDetailStatus: (status: Note['status']) => {
    const { detailNote } = get()
    if (detailNote) {
      set({ detailNote: { ...detailNote, status } })
    }
  },

  // ========== 素材选择器 ==========
  openAssetPicker: (noteId: string, index: number) => {
    set({
      assetPickerOpen: true,
      assetTarget: { noteId, index },
    })
  },

  closeAssetPicker: () => {
    set({
      assetPickerOpen: false,
      assetTarget: { noteId: '', index: -1 },
    })
  },

  // ========== 模版选择器 ==========
  openTemplatePicker: (action: 'bulk' | 'smart', note?: Note) => {
    set({
      templatePickerOpen: true,
      pendingAction: action,
      pendingNote: note || null,
    })
  },

  closeTemplatePicker: () => {
    set({
      templatePickerOpen: false,
      pendingAction: null,
      pendingNote: null,
    })
  },

  // ========== 重置 ==========
  reset: () => {
    set(initialState)
  },
}))

